import { Deg2Rad } from "../../../math/MathEx.js";
import { J2000 } from "../../AstroConstants.js";
export class Orbit {
    // public virtual bool isPeriodic() { return true; }
    isPeriodic() {
        return true;
    }
    // Return the time range over which the orbit is valid; if the orbit
    // is always valid, begin and end should be equal.
    // public virtual void getValidRange(ref double begin, ref double end)
    // { begin = 0.0; end = 0.0; }
    static Obliquity(t) {
        // Parameter t represents the Julian centuries elapsed since 1900.
        // In other words, t = (jd - 2415020.0) / 36525.0
        return (Deg2Rad *
            (2.345229444e1 -
                (((-1.81e-3 * t + 5.9e-3) * t + 4.6845e1) * t) / 3600.0));
    }
    static Nutation(t, m) {
        // Parameter t represents the Julian centuries elapsed since 1900.
        // In other words, t = (jd - 2415020.0) / 36525.0
        let ls, ld; // sun's mean longitude, moon's mean longitude
        let ms, md; // sun's mean anomaly, moon's mean anomaly
        let nm; // longitude of moon's ascending node
        let t2;
        let tls, tnm, tld; // twice above
        let a, b;
        t2 = t * t;
        a = 100.0021358 * t;
        b = 360.0 * (a - Math.floor(a));
        ls = 279.697 + 0.000303 * t2 + b;
        a = 1336.855231 * t;
        b = 360.0 * (a - Math.floor(a));
        ld = 270.434 - 0.001133 * t2 + b;
        a = 99.99736056000026 * t;
        b = 360.0 * (a - Math.floor(a));
        ms = 358.476 - 0.00015 * t2 + b;
        a = 13255523.59 * t;
        b = 360.0 * (a - Math.floor(a));
        md = 296.105 + 0.009192 * t2 + b;
        a = 5.372616667 * t;
        b = 360.0 * (a - Math.floor(a));
        nm = 259.183 + 0.002078 * t2 - b;
        //convert to radian forms for use with trig functions.
        tls = 2 * Deg2Rad * ls;
        nm = Deg2Rad * nm;
        tnm = 2 * Deg2Rad * nm;
        ms = Deg2Rad * ms;
        tld = 2 * Deg2Rad * ld;
        md = Deg2Rad * md;
        // find delta psi and eps, in arcseconds.
        m.dpsi =
            (-17.2327 - 0.01737 * t) * Math.sin(nm) +
                (-1.2729 - 0.00013 * t) * Math.sin(tls) +
                0.2088 * Math.sin(tnm) -
                0.2037 * Math.sin(tld) +
                (0.1261 - 0.00031 * t) * Math.sin(ms) +
                0.0675 * Math.sin(md) -
                (0.0497 - 0.00012 * t) * Math.sin(tls + ms) -
                0.0342 * Math.sin(tld - nm) -
                0.0261 * Math.sin(tld + md) +
                0.0214 * Math.sin(tls - ms) -
                0.0149 * Math.sin(tls - tld + md) +
                0.0124 * Math.sin(tls - nm) +
                0.0114 * Math.sin(tld - md);
        m.deps =
            (9.21 + 0.00091 * t) * Math.cos(nm) +
                (0.5522 - 0.00029 * t) * Math.cos(tls) -
                0.0904 * Math.cos(tnm) +
                0.0884 * Math.cos(tld) +
                0.0216 * Math.cos(tls + ms) +
                0.0183 * Math.cos(tld - nm) +
                0.0113 * Math.cos(tld + md) -
                0.0093 * Math.cos(tls - ms) -
                0.0066 * Math.cos(tls - nm);
        // convert to radians.
        m.dpsi = Deg2Rad * (m.dpsi / 3600);
        m.deps = Deg2Rad * (m.deps / 3600);
    }
    static EclipticToEquatorial(t, fEclLat, fEclLon, outRaDec) {
        // Parameter t represents the Julian centuries elapsed since 1900.
        // In other words, t = (jd - 2415020.0) / 36525.0
        let seps, ceps; // sin and cos of mean obliquity
        let sx, cx, sy, cy, ty;
        let eps;
        // let deps, dpsi;
        t = (J2000 - 2415020.0) / 36525.0;
        t = 0;
        eps = Orbit.Obliquity(t); // mean obliquity for date
        const _m = {
            deps: 0,
            dpsi: 0,
        };
        Orbit.Nutation(t, _m);
        eps += _m.deps;
        seps = Math.sin(eps);
        ceps = Math.cos(eps);
        sy = Math.sin(fEclLat);
        cy = Math.cos(fEclLat); // always non-negative
        if (Math.abs(cy) < 1e-20)
            cy = 1e-20; // insure > 0
        ty = sy / cy;
        cx = Math.cos(fEclLon);
        sx = Math.sin(fEclLon);
        outRaDec.dec = Math.asin(sy * ceps + cy * seps * sx);
        outRaDec.ra = Math.atan((sx * ceps - ty * seps) / cx);
        if (cx < 0) {
            outRaDec.ra += Math.PI; // account for atan quad ambiguity
        }
        outRaDec.ra = Orbit.Pfmod(outRaDec.ra, 2 * Math.PI);
    }
    static Pfmod(x, y) {
        const quotient = Math.floor(Math.abs(x / y));
        if (x < 0.0)
            return x + (quotient + 1) * y;
        else
            return x - quotient * y;
    }
    static EpochConvert(jdFrom, jdTo, a0, d0, m) {
        const T = (jdFrom - J2000) / 36525.0;
        const t = (jdTo - jdFrom) / 36525.0;
        let zeta = (2306.2181 + 1.39656 * T - 0.000139 * T * T) * t +
            (0.30188 - 0.000344 * T) * t * t +
            0.017998 * t * t * t;
        let z = (2306.2181 + 1.39656 * T - 0.000139 * T * T) * t +
            (1.09468 + 0.000066 * T) * t * t +
            0.018203 * t * t * t;
        let theta = (2004.3109 - 0.8533 * T - 0.000217 * T * T) * t -
            (0.42665 + 0.000217 * T) * t * t -
            0.041833 * t * t * t;
        zeta = Deg2Rad * (zeta / 3600.0);
        z = Deg2Rad * (z / 3600.0);
        theta = Deg2Rad * (theta / 3600.0);
        const A = Math.cos(d0) * Math.sin(a0 + zeta);
        const B = Math.cos(theta) * Math.cos(d0) * Math.cos(a0 + zeta) -
            Math.sin(theta) * Math.sin(d0);
        const C = Math.sin(theta) * Math.cos(d0) * Math.cos(a0 + zeta) +
            Math.cos(theta) * Math.sin(d0);
        m.ra = Math.atan2(A, B) + z;
        m.dec = Math.asin(C);
    }
}
